import { Map, List } from "immutable";
import * as actionTypes from "~/store/action_types";
import i18n from "../../i18n";

const realTimeMessages = {
  connecting: i18n.t("Reconnecting..."),
  connected: i18n.t("Hooray, we're back!"), // eslint-disable-line quotes
};

const initialState = Map({
  status: undefined,
  message: undefined,
  type: undefined, // `error` or `success`
  additional: List(),
  timeout: 2500,
  dismissible: true,
});

const httpMessages = (state = initialState, action) => {
  switch (action.type) {
    case actionTypes.FETCH_CONVERSATION_ERROR:
    case actionTypes.RESCHEDULE_TASK_ERROR:
    case actionTypes.RESCHEDULE_TASK_SUCCESS:
    case actionTypes.CLEAR_HTTP_MESSAGES: {
      return initialState;
    }
    case actionTypes.CREATE_STRIPE_TOKEN_ERROR:
      if (action.error) {
        return initialState.merge(
          Map({
            message: action.error,
            type: `error`,
            timeout: 20000,
          })
        );
      }
      return state;
    case actionTypes.ADD_SUCCESS_MESSAGE: {
      return initialState.merge(
        Map({
          message: action.message,
          type: `success`,
          timeout: action.timeout || initialState.get("timeout"),
        })
      );
    }
    case actionTypes.ADD_ERROR_MESSAGE: {
      return initialState.merge(
        Map({
          message: action.message,
          type: `error`,
          timeout: 20000,
        })
      );
    }
    case actionTypes.MOVE_TASK_TO_TRASH_SUCCESS: {
      return initialState.merge(
        Map({
          message: i18n.t("Task successfully moved to Trash"),
          type: `success`,
          timeout: 3000,
        })
      );
    }
    case actionTypes.DUPLICATE_TASK_SUCCESS: {
      return initialState.merge(
        Map({
          message: i18n.t("Task duplicated"),
          type: `success`,
          timeout: 3000,
        })
      );
    }
    case actionTypes.INVITE_USER_CONVERSATION_SUCCESS: {
      const { userIds = [] } = action;
      const count = userIds.length;
      const message = count === 1 ? i18n.t("One participant added.") : `${count} ${i18n.t("participants added.")}`;
      return initialState.merge(
        Map({
          message,
          type: `success`,
          timeout: 3000,
        })
      );
    }
    case actionTypes.REALTIME_CONNECTION_STATE_CHANGE: {
      const { states = {} } = action;
      const { previous, current } = states;

      if ((previous === `connecting` || previous === `unavailable`) && current === `connected`) {
        if (
          state.get(`message`) === realTimeMessages.connecting &&
          state.get(`type`) === `error` &&
          state.get(`timeout`) === 0 &&
          state.get(`dismissible`) === false
        ) {
          return initialState.merge(
            Map({
              message: realTimeMessages.connected,
              type: `success`,
              timeout: 5000,
            })
          );
        }

        return state;
      } else if (
        ((previous === `unavailable` || previous === `connected`) && current === `connecting`) ||
        (previous === "connecting" && current === "unavailable")
      ) {
        return initialState.merge(
          Map({
            message: realTimeMessages.connecting,
            type: `error`,
            timeout: 0,
            dismissible: false,
          })
        );
      }

      return state;
    }
    case actionTypes.STOPWATCH_DELETE_ERROR:
    case actionTypes.STOPWATCH_PAUSE_ERROR:
    case actionTypes.STOPWATCH_RESUME_ERROR:
    case actionTypes.STOPWATCH_START_ERROR: {
      return initialState.merge(
        Map({
          message: i18n.t("Failed to save stopwatch"),
          type: `error`,
          timeout: 5000,
          dismissible: true,
        })
      );
    }
    case actionTypes.ADD_USER_TO_PROJECTS_ERROR: {
      const { projectIds = [] } = action;
      return initialState.merge(
        Map({
          message: i18n.t(`Failed to add user to project${projectIds?.length > 1 ? "s." : "."}`),
          type: `error`,
          timeout: 5000,
          dismissible: true,
        })
      );
    }
    case actionTypes.ADD_PROJECT_TEMPLATE_ERROR: {
      return initialState.merge(
        Map({
          message: i18n.t(`Unable to create Project Template`),
          type: `error`,
          timeout: 5000,
          dismissible: true,
        })
      );
    }
    case actionTypes.EDIT_PROJECT_TEMPLATE_ERROR: {
      return initialState.merge(
        Map({
          message: i18n.t(`Unable to rename Template`),
          type: `error`,
          timeout: 5000,
          dismissible: true,
        })
      );
    }
    case actionTypes.DUPLICATE_PROJECT_TEMPLATE_ERROR: {
      return initialState.merge(
        Map({
          message: i18n.t(`Unable to duplicate Template`),
          type: `error`,
          timeout: 5000,
          dismissible: true,
        })
      );
    }
    case actionTypes.MOVE_PROJECT_TEMPLATE_TO_TRASH_ERROR: {
      return initialState.merge(
        Map({
          message: i18n.t(`Unable to delete Template`),
          type: `error`,
          timeout: 5000,
          dismissible: true,
        })
      );
    }
    case actionTypes.ADD_PROJECT_TEMPLATE_ELEMENT_ERROR: {
      return initialState.merge(
        Map({
          message: i18n.t(`Unable to add`),
          type: `error`,
          timeout: 5000,
          dismissible: true,
        })
      );
    }
    case actionTypes.EDIT_PROJECT_TEMPLATE_ELEMENT_ERROR: {
      return initialState.merge(
        Map({
          message: i18n.t(`Unable to edit`),
          type: `error`,
          timeout: 5000,
          dismissible: true,
        })
      );
    }
    case actionTypes.DELETE_PROJECT_TEMPLATE_ELEMENT_ERROR: {
      return initialState.merge(
        Map({
          message: i18n.t(`Unable to delete`),
          type: `error`,
          timeout: 5000,
          dismissible: true,
        })
      );
    }
    case actionTypes.REORDER_PROJECT_TEMPLATE_LISTS_ERROR: {
      return initialState.merge(
        Map({
          message: i18n.t(`Unable to reorder task lists`),
          type: `error`,
          timeout: 5000,
          dismissible: true,
        })
      );
    }
    case actionTypes.ADD_CONVERSATION_MESSAGE_ERROR:
    case actionTypes.EDIT_CONVERSATION_MESSAGE_ERROR: {
      return initialState.merge(
        Map({
          message: i18n.t("Failed to send a message"),
          type: `error`,
          timeout: 5000,
          dismissible: true,
        })
      );
    }
    case actionTypes.STOPWATCH_EDIT_ERROR: {
      return initialState.merge(
        Map({
          message: i18n.t("Failed to update stopwatch"),
          type: `error`,
          timeout: 5000,
          dismissible: true,
        })
      );
    }
    case actionTypes.RESCHEDULE_TASK_LOADING:
      return initialState.merge(
        Map({
          message: i18n.t("Rescheduling..."),
          type: `success`,
          timeout: 60000,
          dismissible: false,
        })
      );
    case actionTypes.DUPLICATE_TASK_LIST_SUCCESS: {
      return initialState.merge(
        Map({
          message: i18n.t("Task list duplicated"),
          type: `success`,
          timeout: 3000,
        })
      );
    }
    case actionTypes.ADD_USER_INTERNAL_RATE_ERROR:
    case actionTypes.EDIT_USER_WORKWEEK_ERROR: {
      return initialState.merge(
        Map({
          message: i18n.t("There was a problem in updating rates and capacity."),
          type: `error`,
          timeout: 3000,
        })
      );
    }
    case actionTypes.ADD_TIME_RECORD_ERROR:
    case actionTypes.FETCH_LAST_VISIT_ERROR:
    case actionTypes.FETCH_PROJECT_ERROR:
    case actionTypes.LOAD_FAILURE:
    case actionTypes.FETCH_CALENDAR_ERROR:
    case actionTypes.FETCH_CALENDARS_ERROR:
    case actionTypes.FETCH_CALENDAR_EVENT_ERROR:
    case actionTypes.ADD_CALENDAR_EVENT_ERROR:
    case actionTypes.DELETE_CALENDAR_ERROR:
    case actionTypes.DELETE_CALENDAR_EVENT_ERROR:
    case actionTypes.EDIT_CALENDAR_EVENT_ERROR:
    case actionTypes.REFRESH_EXTERNAL_CALENDAR_ERROR:
    case actionTypes.ADD_CALENDAR_ERROR:
    case actionTypes.ADD_EXTERNAL_CALENDAR_ERROR:
    case actionTypes.RESCHEDULE_ONLY_THIS_CALENDAR_EVENT_ERROR:
    case actionTypes.RESCHEDULE_THIS_AND_FUTURE_CALENDAR_EVENTS_ERROR:
    case actionTypes.RESCHEDULE_ALL_CALENDAR_EVENTS_ERROR:
    case actionTypes.RESCHEDULE_EXCEPTION_CALENDAR_EVENT_ERROR:
    case actionTypes.ADD_CALENDAR_EVENT_EXCLUDED_DATES_ERROR: {
      // ignore this cases
      return state;
    }
    default: {
      if (action.error) {
        const { error = {} } = action;
        const { message = `Unknown error`, response = {}, status_code = 500, name = "" } = error;

        if (status_code !== 404) {
          if (name.toLowerCase() === "storageoverusederror") {
            return initialState.merge(
              Map({
                status: 400,
                message: i18n.t("Disk is full!"),
                type: `error`,
                timeout: 20000,
                stack: Map({
                  storageOverused: Map(error),
                }),
              })
            );
          }

          return initialState.merge(
            Map({
              status: status_code,
              message: response.message || message,
              type: `error`,
              timeout: 20000,
            })
          );
        }

        return state;
      }
      return state;
    }
  }
};

export default httpMessages;
